/**
 * 🎯 CIP Provider
 * YetsanCIP SDK'yı React uygulamasına entegre eder
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';

// SDK'yı import et
import '../lib/YetsanCIP';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * CIP Provider Component
 * App'in en üst seviyesinde kullanılır
 */
export const CIPProvider = ({ children }) => {
  const { user, token } = useAuth();
  const location = useLocation();
  const initializedRef = useRef(false);
  const lastPageRef = useRef(null);
  const identifiedRef = useRef(null);

  // SDK'yı konfigüre et
  useEffect(() => {
    if (!window.YetsanCIP || initializedRef.current) return;

    console.log('[CIP] Initializing SDK...');
    
    window.YetsanCIP.push(['config', {
      api_endpoint: API_URL,
      debug: process.env.NODE_ENV === 'development',
      batch_size: 20,
      batch_interval: 10000,
      heartbeat_interval: 60000,
      auto_track: {
        page_views: true,
        clicks: true,
        forms: true,
        scroll: true,
        visibility: true,
        clipboard: true,
        errors: true
      }
    }]);

    initializedRef.current = true;
    console.log('[CIP] SDK initialized');
  }, []);

  // Yalnız MES'e giren hesap: izleme kapalı (müşteri zekâsı ucu bu hesaba sunucuda da kapalı — boşuna 403 üretmesin)
  const yalnizMes = user?.kullanici_tipi === 'mes';
  useEffect(() => { window.YetsanCIP?.sustur?.(yalnizMes); }, [yalnizMes]);

  // Kullanıcı değiştiğinde identify et
  useEffect(() => {
    if (!window.YetsanCIP || !user || !token || yalnizMes) return;
    
    // Customer ID - farklı user yapılarını destekle
    const customerId = user.cari_kodu || user.panel_kodu || user._id || user.id || user.email;
    
    // Aynı kullanıcıyı tekrar identify etme
    if (identifiedRef.current === customerId) return;

    console.log('[CIP] Identifying user:', customerId, 'from user:', user);
    
    window.YetsanCIP.identify({
      customer_id: customerId,
      name: user.cari_unvan || user.name || user.ad || user.email,
      email: user.email,
      type: user.role === 'customer' ? 'customer' : 'staff',
      firma: user.firma,
      panel_kodu: user.panel_kodu
    });
    
    identifiedRef.current = customerId;
  }, [user, token, yalnizMes]);

  // Sayfa değişikliklerini takip et
  useEffect(() => {
    if (!window.YetsanCIP || !initializedRef.current) return;
    
    const currentPath = location.pathname;
    if (lastPageRef.current === currentPath) return;
    
    lastPageRef.current = currentPath;
    
    // Sayfa tipini belirle
    const pageType = getPageType(currentPath);
    
    console.log('[CIP] Page view:', pageType, currentPath);
    
    window.YetsanCIP.page(pageType, {
      path: currentPath,
      search: location.search
    });
  }, [location.pathname, location.search]);

  // Logout durumunda reset
  useEffect(() => {
    if (!user && identifiedRef.current) {
      console.log('[CIP] User logged out, resetting identity');
      identifiedRef.current = null;
      // Session'ı sona erdirme - SDK bunu otomatik yapar
    }
  }, [user]);

  return children;
};

/**
 * Sayfa tipini belirle
 */
function getPageType(path) {
  if (path === '/' || path === '/panel' || path === '/panel/dashboard') return 'home';
  if (path.includes('/urunler')) return 'product_list';
  if (path.includes('/urun/')) return 'product_detail';
  if (path.includes('/sepet')) return 'cart';
  if (path.includes('/siparis')) return 'checkout';
  if (path.includes('/hesabim')) return 'account';
  if (path.includes('/arama')) return 'search';
  if (path.includes('/kategori')) return 'category';
  if (path.includes('/analitik') || path.includes('/cip')) return 'analytics';
  if (path.includes('/wms')) return 'wms';
  if (path.includes('/yonetim')) return 'admin';
  if (path.includes('/teklif')) return 'quote';
  return 'other';
}

/**
 * CIP Tracking Utilities
 * Component'lerde kullanım için export edilen fonksiyonlar
 */
export const CIP = {
  track: (eventName, data = {}) => {
    if (window.YetsanCIP) {
      window.YetsanCIP.track(eventName, data);
    }
  },
  
  trackProduct: (sku, name, price, data = {}) => {
    if (window.YetsanCIP) {
      window.YetsanCIP.trackProduct(sku, name, price, data);
    }
  },
  
  trackSearch: (query, resultCount, results = []) => {
    if (window.YetsanCIP) {
      window.YetsanCIP.trackSearch(query, resultCount, results);
    }
  },
  
  trackCartAdd: (sku, name, quantity, price) => {
    if (window.YetsanCIP) {
      window.YetsanCIP.trackCartAdd(sku, name, quantity, price);
    }
  },
  
  trackCartRemove: (sku, quantity) => {
    if (window.YetsanCIP) {
      window.YetsanCIP.trackCartRemove(sku, quantity);
    }
  },
  
  trackPurchase: (orderId, totalValue, items = []) => {
    if (window.YetsanCIP) {
      window.YetsanCIP.trackPurchase(orderId, totalValue, items);
    }
  }
};

export default CIPProvider;
